import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { X, Check, Cookie, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';

// Define a custom event type
declare global {
  interface WindowEventMap {
    'openCookieConsent': CustomEvent;
  }
  interface Window {
    showCookieConsent: () => void;
  }
}

type CookiePreferences = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
};

export const CookieConsent: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always true and can't be disabled
    analytics: false,
    marketing: false,
  });

  // Check localStorage on mount to see if user has already set preferences
  useEffect(() => {
    const savedPreferences = localStorage.getItem('cookieConsent');
    
    if (!savedPreferences) {
      // If no preferences found, show the banner
      setIsOpen(true);
    } else {
      // If preferences found, parse and set them
      try {
        const parsedPreferences = JSON.parse(savedPreferences);
        setPreferences(parsedPreferences);
      } catch (error) {
        console.error('Error parsing cookie preferences:', error);
        setIsOpen(true);
      }
    }
  }, []);

  // Expose showCookieConsent to window for Footer link
  useEffect(() => {
    window.showCookieConsent = () => {
      setIsOpen(true);
      setShowCustomize(true); // Open directly to customization
    };
    
    const handleOpen = () => {
      setIsOpen(true);
    };
    
    window.addEventListener('openCookieConsent', handleOpen);
    
    return () => {
      window.removeEventListener('openCookieConsent', handleOpen);
      delete (window as any).showCookieConsent;
    };
  }, []);

  const savePreferences = (newPreferences: CookiePreferences) => {
    localStorage.setItem('cookieConsent', JSON.stringify(newPreferences));
    setPreferences(newPreferences);
    setIsOpen(false);
    setShowCustomize(false);
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    savePreferences(allAccepted);
  };

  const handleRejectNonEssential = () => {
    const essentialOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    savePreferences(essentialOnly);
  };

  const handleCustomizeClick = () => {
    setShowCustomize(true);
  };

  const handleSaveCustomized = () => {
    savePreferences(preferences);
  };

  const handlePreferenceChange = (category: keyof CookiePreferences) => {
    if (category === 'necessary') return; // Can't change necessary cookies
    
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          className="fixed inset-0 flex items-center justify-center z-[100] p-4"
        >
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setIsOpen(false)}></div>
          <div className="w-full max-w-lg z-10 relative rounded-3xl border border-white/10 bg-[#0A0B10] text-white shadow-2xl p-8 ring-1 ring-white/5">
            {!showCustomize ? (
              <>
                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 mr-4">
                      <Cookie className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold tracking-tight text-white">{t('cookies.cookieConsent.title')}</h3>
                      <p className="text-[10px] text-slate-500 uppercase tracking-widest font-mono mt-1">Privacy & Data Control</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-slate-500 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-lg"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="prose prose-invert prose-sm max-w-none mb-8">
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {t('cookies.cookieConsent.description')}
                  </p>
                  <p className="text-slate-500 text-xs mt-3 bg-white/5 p-3 rounded-lg border border-white/5">
                    <strong className="text-slate-300">{t('cookies.cookieConsent.dataProcessing.title')}</strong> {t('cookies.cookieConsent.dataProcessing.content')}
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-end">
                  <button
                    onClick={handleRejectNonEssential}
                    className="px-5 py-2.5 rounded-xl border border-white/10 bg-transparent text-slate-300 hover:bg-white/5 hover:text-white transition-all text-sm font-medium"
                  >
                    {t('cookies.cookieConsent.buttons.rejectAll')}
                  </button>
                  <button
                    onClick={handleCustomizeClick}
                    className="px-5 py-2.5 rounded-xl border border-white/10 bg-transparent text-slate-300 hover:bg-white/5 hover:text-white transition-all text-sm font-medium flex items-center justify-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    {t('cookies.cookieConsent.buttons.customize')}
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="px-6 py-2.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-black font-bold transition-all text-sm shadow-[0_0_20px_-5px_rgba(6,182,212,0.5)]"
                  >
                    {t('cookies.cookieConsent.buttons.acceptAll')}
                  </button>
                </div>
                <div className="mt-6 pt-4 border-t border-white/5 text-center">
                  <Link to="/cookies" className="text-xs text-slate-500 hover:text-cyan-400 transition-colors">
                    Read full Cookie Policy
                  </Link>
                </div>
              </>
            ) : (
              <>
                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 mr-4">
                      <Settings className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold tracking-tight text-white">{t('cookies.cookieConsent.buttons.customize')}</h3>
                      <p className="text-[10px] text-slate-500 uppercase tracking-widest font-mono mt-1">Granular Control</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowCustomize(false)}
                    className="text-slate-500 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-lg"
                    aria-label="Back"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="space-y-4 mb-8 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                  {/* Necessary cookies */}
                  <div className="p-4 rounded-2xl border border-white/10 bg-white/5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 flex items-center justify-center rounded bg-cyan-500 text-black`}>
                          <Check className="w-3 h-3" strokeWidth={4} />
                        </div>
                        <span className="text-sm font-bold text-white">{t('cookies.cookieConsent.categories.necessary.title')}</span>
                      </div>
                      <span className="text-[10px] px-2 py-1 rounded-full bg-cyan-500/10 text-cyan-400 tracking-wider uppercase border border-cyan-500/20">
                        {t('cookies.cookieConsent.mandatoryLabel', 'Mandatory')}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      {t('cookies.cookieConsent.categories.necessary.description')} Includes CRM data retention for communication continuity.
                    </p>
                  </div>
                  
                  {/* Analytics cookies */}
                  <div className="p-4 rounded-2xl border border-white/10 bg-transparent hover:bg-white/[0.02] transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handlePreferenceChange('analytics')}
                          className={`w-5 h-5 flex items-center justify-center rounded transition-all ${preferences.analytics ? 'bg-cyan-500 text-black' : 'bg-slate-800 border border-slate-600'}`}
                        >
                          {preferences.analytics && <Check className="w-3 h-3" strokeWidth={4} />}
                        </button>
                        <span className="text-sm font-bold text-white">{t('cookies.cookieConsent.categories.analytics.title')}</span>
                      </div>
                      <span className="text-[10px] px-2 py-1 rounded-full bg-slate-800 text-slate-400 tracking-wider uppercase border border-slate-700">
                        {t('cookies.cookieConsent.optionalLabel', 'Optional')}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      {t('cookies.cookieConsent.categories.analytics.description')}
                    </p>
                  </div>
                  
                  {/* Marketing cookies */}
                  <div className="p-4 rounded-2xl border border-white/10 bg-transparent hover:bg-white/[0.02] transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handlePreferenceChange('marketing')}
                          className={`w-5 h-5 flex items-center justify-center rounded transition-all ${preferences.marketing ? 'bg-cyan-500 text-black' : 'bg-slate-800 border border-slate-600'}`}
                        >
                          {preferences.marketing && <Check className="w-3 h-3" strokeWidth={4} />}
                        </button>
                        <span className="text-sm font-bold text-white">{t('cookies.cookieConsent.categories.marketing.title')}</span>
                      </div>
                      <span className="text-[10px] px-2 py-1 rounded-full bg-slate-800 text-slate-400 tracking-wider uppercase border border-slate-700">
                        {t('cookies.cookieConsent.optionalLabel', 'Optional')}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      {t('cookies.cookieConsent.categories.marketing.description')}
                    </p>
                  </div>
                </div>
                
                <div className="flex justify-end pt-4 border-t border-white/5">
                  <button
                    onClick={handleSaveCustomized}
                    className="px-6 py-2.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-black font-bold transition-all text-sm shadow-[0_0_20px_-5px_rgba(6,182,212,0.5)]"
                  >
                    {t('cookies.cookieConsent.save')}
                  </button>
                </div>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}; 